"use client";

import { usePressedPitchClasses } from "@/hooks/use-pressed-pitch-classes";
import { useNoteLabel } from "@/hooks/use-notation";
import {
  formatAccidentals,
  pitchClassToName,
  ROOT_PITCH_CLASS,
} from "@/lib/music/theory-helpers";
import {
  BORDER,
  FONT_MONO,
  SURFACE,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "@/components/ui/organisms/chord-practice/palette";
import { getMajorFingering } from "./harmony-fingerings";
import { ScaleFrieze } from "./harmony-scale-frieze";
import { LEXICON, Term } from "./harmony-term";
import type { HarmonyTonality } from "./types";

interface HarmonyScaleDetailProps {
  tonality: HarmonyTonality;
}

const SCALE_INTERVALS_MAJOR = [0, 2, 4, 5, 7, 9, 11];
const SCALE_INTERVALS_MINOR = [0, 2, 3, 5, 7, 8, 10];

const HAND_RIGHT = "var(--color-hand-right)";
const HAND_LEFT = "var(--color-hand-left)";
const THUMB_HIGHLIGHT = "#fbbf24";

/**
 * Detailed view for the «&nbsp;scale&nbsp;» focus inside the Harmony tab.
 *
 * Sober pedagogical card: the same `ScaleFrieze` already used in the
 * overview, plus a per-degree breakdown (label, French solfège, role).
 * The piano is decorated by `useScalePianoHints` upstream, so this
 * component stays purely presentational.
 *
 * Volontarily light, the full scale-practice (fingerings, tempo runner,
 * scale-roll) is not ported in Phase C. Cf. open questions for Phase D+.
 */
export function HarmonyScaleDetail({ tonality }: HarmonyScaleDetailProps) {
  const tonicPc = ROOT_PITCH_CLASS[tonality.tonic] ?? 0;
  const intervals =
    tonality.mode === "major" ? SCALE_INTERVALS_MAJOR : SCALE_INTERVALS_MINOR;

  const toNoteLabel = useNoteLabel();
  const tonicLabel = toNoteLabel(tonality.tonic);
  const modeLabel = tonality.mode === "major" ? "majeur" : "mineur";

  const pressedPcs = usePressedPitchClasses();

  // Notes de la gamme + reprise de la tonique à l'octave (8 cases pour les
  // doigtés). Le doigté est null en mode mineur ou tonalité non couverte
  //, on masque alors la section.
  const scaleNoteNames = intervals.map((iv) =>
    pitchClassToName((tonicPc + iv) % 12, tonality.tonic),
  );
  const fingeringNotes = [...scaleNoteNames, scaleNoteNames[0]!];
  const fingering =
    tonality.mode === "major" ? getMajorFingering(tonality.tonic) : null;

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        padding: "32px 40px 48px",
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 30% 0%, ${VIOLET_SOFT} 0%, transparent 55%)`,
          pointerEvents: "none",
        }}
      />

      <header style={{ position: "relative", zIndex: 1, marginBottom: 28 }}>
        <div
          style={{
            fontSize: 10,
            fontFamily: FONT_MONO,
            color: VIOLET_BRIGHT,
            opacity: 0.6,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          Détail de la gamme · {tonicLabel} {modeLabel}
        </div>
        <h1
          style={{
            fontSize: 28,
            fontFamily: "Inter, sans-serif",
            fontWeight: 300,
            color: TEXT,
            letterSpacing: "-0.02em",
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          La gamme de{" "}
          <span
            style={{
              fontWeight: 500,
              background: `linear-gradient(135deg, ${VIOLET_BRIGHT}, ${VIOLET})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {tonicLabel} {modeLabel}
          </span>
        </h1>
        <p
          style={{
            fontSize: 13,
            color: TEXT_DIM,
            fontFamily: "Inter, sans-serif",
            margin: "8px 0 0",
            maxWidth: 580,
            lineHeight: 1.5,
          }}
        >
          Sept <Term tip={LEXICON.degree}>degrés</Term> espacés par une
          alternance de <Term tip={LEXICON.tone}>tons</Term> et{" "}
          <Term tip={LEXICON.semitone}>demi-tons</Term>, c&apos;est cette
          structure qui donne sa couleur au mode.
        </p>
      </header>

      <section
        style={{
          position: "relative",
          zIndex: 1,
          padding: "20px 22px 22px",
          borderRadius: 14,
          border: `1px solid ${VIOLET_GLOW}`,
          background:
            "linear-gradient(180deg, rgba(139,92,246,0.06), rgba(255,255,255,0.02))",
          boxShadow: "0 0 32px rgba(139,92,246,0.08)",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 9,
            color: TEXT_MUTE,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Anatomie de la gamme
        </div>
        <ScaleFrieze
          tonic={tonality.tonic}
          semitones={intervals}
          flavor={tonality.mode}
          density="compact"
          activePitchClasses={pressedPcs}
        />
      </section>

      {fingering ? (
        <section
          style={{
            position: "relative",
            zIndex: 1,
            padding: "20px 22px 22px",
            borderRadius: 14,
            border: `1px solid ${BORDER}`,
            background: SURFACE_LO,
            marginTop: 24,
          }}
        >
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 9,
              color: TEXT_MUTE,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Doigtés
          </div>

          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              color: TEXT_DIM,
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                marginRight: 8,
                borderRadius: 4,
                background:
                  "color-mix(in srgb, #fbbf24 18%, transparent)",
                border: "1px solid rgba(251,191,36,0.4)",
                color: THUMB_HIGHLIGHT,
                fontFamily: FONT_MONO,
                fontSize: 12,
                fontWeight: 700,
                verticalAlign: "middle",
              }}
            >
              1
            </span>
            Les cases en jaune marquent un{" "}
            <strong style={{ color: THUMB_HIGHLIGHT }}>
              passage du pouce
            </strong>{" "}
           , il passe sous les autres doigts pour enchaîner les notes sans
            rupture.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FingeringRow
              label="Main droite (asc.)"
              notes={fingeringNotes}
              fingers={fingering.rh}
              thumbCrossings={fingering.rhCrossings}
              color={HAND_RIGHT}
            />
            <FingeringRow
              label="Main gauche (asc.)"
              notes={fingeringNotes}
              fingers={fingering.lh}
              thumbCrossings={fingering.lhCrossings}
              color={HAND_LEFT}
            />
          </div>
        </section>
      ) : (
        <section
          style={{
            position: "relative",
            zIndex: 1,
            padding: "16px 22px",
            borderRadius: 14,
            border: `1px dashed ${BORDER}`,
            background: SURFACE_LO,
            marginTop: 24,
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: TEXT_MUTE,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: TEXT_DIM, fontWeight: 500 }}>
            Doigtés
          </strong>{" "}
         , non disponibles pour cette tonalité ({tonicLabel} {modeLabel}).
          Les doigtés mineurs varient selon la forme (naturelle, harmonique,
          mélodique) et seront ajoutés ultérieurement.
        </section>
      )}
    </div>
  );
}

function FingeringRow({
  label,
  notes,
  fingers,
  thumbCrossings,
  color,
}: {
  label: string;
  notes: string[];
  fingers: readonly number[];
  thumbCrossings: readonly number[];
  color: string;
}) {
  const toNoteLabel = useNoteLabel();

  return (
    <div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          letterSpacing: "0.1em",
          color,
          textTransform: "uppercase",
          marginBottom: 6,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {notes.map((n, i) => {
          const finger = fingers[i % fingers.length];
          const isThumbCrossing = thumbCrossings.includes(i);

          return (
            <div
              key={i}
              title={
                isThumbCrossing
                  ? "Passage du pouce, il passe sous le doigt précédent pour continuer la gamme sans rupture."
                  : undefined
              }
              style={{
                flex: 1,
                padding: "6px 8px",
                borderRadius: 8,
                position: "relative",
                background: isThumbCrossing
                  ? "color-mix(in srgb, #fbbf24 14%, transparent)"
                  : SURFACE,
                border: `1px solid ${isThumbCrossing ? "rgba(251,191,36,0.4)" : BORDER}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                cursor: isThumbCrossing ? "help" : "default",
              }}
            >
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  color: TEXT,
                }}
              >
                {toNoteLabel(n)}
              </span>
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 16,
                  fontWeight: 700,
                  color: isThumbCrossing ? THUMB_HIGHLIGHT : color,
                }}
              >
                {finger}
              </span>
              {isThumbCrossing && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 3,
                    right: 5,
                    fontSize: 10,
                    color: THUMB_HIGHLIGHT,
                    opacity: 0.85,
                    lineHeight: 1,
                  }}
                >
                  ↶
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
